import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tab,
  Tabs,
  Snackbar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
// Box import removed for Material-UI v7 compatibility
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Store as StoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/layout/AdminLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

interface StoreSettings {
  storeName: string;
  storeDescription: string;
  storeEmail: string;
  storePhone: string;
  storeAddress: string;
  currency: string;
  timezone: string;
  language: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  orderNotifications: boolean;
  lowStockAlerts: boolean;
  customerMessages: boolean;
}

interface SecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordPolicy: string;
  ipWhitelist: string[];
}

const AdminSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    storeName: 'DEWFOAM',
    storeDescription: 'Premium Furniture Store',
    storeEmail: 'support@dewfoam.ph',
    storePhone: '+63 2 1234 5678',
    storeAddress: 'Metro Manila, Philippines',
    currency: 'PHP',
    timezone: 'Asia/Manila',
    language: 'en',
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    pushNotifications: true,
    orderNotifications: true,
    lowStockAlerts: true,
    customerMessages: false,
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordPolicy: 'strong',
    ipWhitelist: ['192.168.1.1', '10.0.0.1'],
  });

  const handleSaveSettings = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setSnackbarMessage('Settings saved successfully.');
      setSnackbarOpen(true);
      setLoading(false);
    }, 1000);
  };

  const handleAddIP = () => {
    const newIP = prompt('Please enter IP address to add:');
    if (newIP) {
      setSecuritySettings({
        ...securitySettings,
        ipWhitelist: [...securitySettings.ipWhitelist, newIP],
      });
    }
  };

  const handleRemoveIP = (index: number) => {
    setSecuritySettings({
      ...securitySettings,
      ipWhitelist: securitySettings.ipWhitelist.filter((_, i) => i !== index),
    });
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#333',
            mb: 1,
          }}
        >
          Settings
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage system and store settings
        </Typography>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{ px: 3 }}
          >
            <Tab
              icon={<StoreIcon />}
              label="Store Settings"
              iconPosition="start"
              sx={{ minHeight: 'auto', py: 2 }}
            />
            <Tab
              icon={<NotificationsIcon />}
              label="Notification Settings"
              iconPosition="start"
              sx={{ minHeight: 'auto', py: 2 }}
            />
            <Tab
              icon={<SecurityIcon />}
              label="Security Settings"
              iconPosition="start"
              sx={{ minHeight: 'auto', py: 2 }}
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {/* Store Settings Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 4 }}>
              <Box>
                <TextField
                  fullWidth
                  label="Store Name"
                  value={storeSettings.storeName}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storeName: e.target.value })
                  }
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Store Description"
                  value={storeSettings.storeDescription}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storeDescription: e.target.value })
                  }
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={storeSettings.storeEmail}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storeEmail: e.target.value })
                  }
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={storeSettings.storePhone}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storePhone: e.target.value })
                  }
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={storeSettings.storeAddress}
                  onChange={(e) =>
                    setStoreSettings({ ...storeSettings, storeAddress: e.target.value })
                  }
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Regional Settings
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={storeSettings.currency}
                    label="Currency"
                    onChange={(e) =>
                      setStoreSettings({ ...storeSettings, currency: e.target.value })
                    }
                  >
                    <MenuItem value="PHP">Philippine Peso (PHP)</MenuItem>
                    <MenuItem value="USD">US Dollar (USD)</MenuItem>
                    <MenuItem value="EUR">Euro (EUR)</MenuItem>
                    <MenuItem value="KRW">Korean Won (KRW)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={storeSettings.timezone}
                    label="Timezone"
                    onChange={(e) =>
                      setStoreSettings({ ...storeSettings, timezone: e.target.value })
                    }
                  >
                    <MenuItem value="Asia/Manila">Asia/Manila</MenuItem>
                    <MenuItem value="Asia/Seoul">Asia/Seoul</MenuItem>
                    <MenuItem value="America/New_York">America/New York</MenuItem>
                    <MenuItem value="Europe/London">Europe/London</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={storeSettings.language}
                    label="Language"
                    onChange={(e) =>
                      setStoreSettings({ ...storeSettings, language: e.target.value })
                    }
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="ko">Korean</MenuItem>
                    <MenuItem value="ja">Japanese</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </TabPanel>

          {/* Notification Settings Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Notification Settings
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        emailNotifications: e.target.checked,
                      })
                    }
                  />
                }
                label="Email Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.pushNotifications}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        pushNotifications: e.target.checked,
                      })
                    }
                  />
                }
                label="Push Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.orderNotifications}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        orderNotifications: e.target.checked,
                      })
                    }
                  />
                }
                label="Order Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.lowStockAlerts}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        lowStockAlerts: e.target.checked,
                      })
                    }
                  />
                }
                label="Low Stock Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.customerMessages}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        customerMessages: e.target.checked,
                      })
                    }
                  />
                }
                label="Customer Message Notifications"
              />
            </Box>
          </TabPanel>

          {/* Security Settings Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Security Settings
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={securitySettings.twoFactorAuth}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        twoFactorAuth: e.target.checked,
                      })
                    }
                  />
                }
                label="Enable Two-Factor Authentication"
              />

              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Session Timeout (minutes)
                </Typography>
                <TextField
                  type="number"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) =>
                    setSecuritySettings({
                      ...securitySettings,
                      sessionTimeout: parseInt(e.target.value) || 30,
                    })
                  }
                  sx={{ width: 200 }}
                />
              </Box>

              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Password Policy
                </Typography>
                <FormControl sx={{ width: 200 }}>
                  <Select
                    value={securitySettings.passwordPolicy}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        passwordPolicy: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="strong">Strong</MenuItem>
                    <MenuItem value="very_strong">Very Strong</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    IP Whitelist
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddIP}
                    sx={{ borderColor: '#35C5F0', color: '#35C5F0' }}
                  >
                    Add IP
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {securitySettings.ipWhitelist.map((ip, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={ip} />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveIP(index)}
                            sx={{ color: '#F44336' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            </Box>
          </TabPanel>

          {/* Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={loading}
              sx={{
                backgroundColor: '#35C5F0',
                '&:hover': { backgroundColor: '#2ba3cc' },
                px: 4,
                py: 1.5,
              }}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </AdminLayout>
  );
};

export default AdminSettings;